import React, { useEffect, useState, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import Select from 'react-select'
import TextInput from '../TextInput'
import Grid from '../Grid'
import Button from '../Button'
import Typography from '../Typography'
import Image from '../Image'
import { CustomerContext } from '../../contexts/CustomerContext'
import useSchools from '../../hooks/useSchools'
import Cart from '../../models/Cart'
import frontPageFormImg from '../../assets/front_page_form_img.png'
import styles from './HomePageForm.module.sass'
import HomeCTASection from '../HomeCTASection'
import useStudentImageStatus from './useStudentImageStatus'

function validateEmail(email) {
  if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
    return true
  }

  return false
}

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? '#000' : '#777',
    fontFamily: 'inherit',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#777',
    fontFamily: 'inherit',
    fontSize: '0.95em',
  }),
}

const HomePageForm = ({ isMakingAppt }) => {
  const { customer, setCustomer } = useContext(CustomerContext)
  const [email, setEmail] = useState(customer.email || '')
  const [school, setSchool] = useState(customer.currentSchool ? customer.currentSchool.name : '')
  const [errors, setErrors] = useState([])
  const [existingCart, setExistingCart] = useState(null)
  const schools = useSchools()
  const { status: studentImageStatus, loading: loadingStatuses } = useStudentImageStatus(school, schools)
  const history = useHistory()

  useEffect(() => {
    if (!customer.cart) return

    Cart
      .where({ for_email: [customer.cart.id, customer.email] })
      .first()
      .then((response) => {
        if (!response.data) return;
        if (!response.data.purchased) setExistingCart(response.data)
    })
  }, [])

  const emailHandler = (e) => setEmail(e.target.value)
  const schoolHandler = (e) => setSchool(e.label)

  const submitHandler = (e) => {
    e.preventDefault()

    localStorage.setItem('orderingAhead', false)
    const chosenSchool = schools.find((item) => item.name.trim() === school.trim())

    if (!chosenSchool) {
      setErrors(['Please select a school from the dropdown'])
      return
    }

    if (!email) {
      setErrors(['Please enter a valid email'])
      return
    }

    if (errors.length > 0) setErrors([])

    const nextPage = `student_id_entry${isMakingAppt ? '?app=true' : ''}`
    const { apptCart } = customer

    if (isMakingAppt && apptCart) {
      if (chosenSchool.id !== apptCart.schoolId) {
        apptCart.schoolId = chosenSchool.id

        apptCart
          .save()
          .then(() => history.push(nextPage))
      } else {
        history.push(nextPage)
      }
    } else if (existingCart && !isMakingAppt) {
      // TODO: will need to check if customer changes this info and update accordingly
      setCustomer({
        ...customer, cart: existingCart, email, currentSchool: chosenSchool,
      })

      setTimeout(() => history.push(nextPage), 75)
    } else {
      const cart = new Cart({ school: chosenSchool, schoolId: chosenSchool.id, email })

      cart.save().then(() => {
        const key = isMakingAppt ? 'apptCart' : 'cart'

        setCustomer({
          ...customer, [key]: cart, email, currentSchool: chosenSchool,
        })

        setTimeout(() => history.push(nextPage), 75)
      })
    }
  }

  const handlePreorder = () => {
    const chosenSchool = schools.find((item) => item.name.trim() === school.trim())

    if (!chosenSchool) {
      setErrors(['Please select a school from the dropdown'])
      return
    }

    if (!validateEmail(email)) {
      setErrors(['Please enter a valid email'])
      return
    }

    if (errors.length > 0) setErrors([])

    const nextRoute = 'student_id_entry'

    localStorage.setItem('orderingAhead', false)

    if (existingCart) {
      setCustomer({
        ...customer, cart: existingCart, email, currentSchool: chosenSchool,
      })

      setTimeout(() => history.push(nextRoute), 75)
    } else {
      const cart = new Cart({ school: chosenSchool, schoolId: chosenSchool.id, email })

      cart.save().then(() => {
        const key = isMakingAppt ? 'apptCart' : 'cart'

        setCustomer({
          ...customer, [key]: cart, email, currentSchool: chosenSchool,
        })

        setTimeout(() => history.push(nextRoute), 75)
      })
    }
  }

  const handleOrderStatus = (e) => {
    e.preventDefault();

    setTimeout(() => history.push('order-status'), 75)
  }

  const handleAccessCode = (e) => {
    e.preventDefault();

    const chosenSchool = schools.find((item) => item.name.trim() === school.trim())

    if (!chosenSchool) {
      setErrors(['Please select a school from the dropdown'])
      return
    }

    setTimeout(() => history.push('get-access-code'), 75)
  }

  const handleYearbookOrder = () => {
    const chosenSchool = schools.find((item) => item.name.trim() === school.trim())

    if (!chosenSchool) {
      setErrors(['Please select a school from the dropdown'])
      return
    }

    if (!validateEmail(email)) {
      setErrors(['Please enter a valid email'])
      return
    }

    if (errors.length > 0) setErrors([])

    if (existingCart) {
      setCustomer({
        ...customer, cart: existingCart, email, currentSchool: chosenSchool,
      })

      setTimeout(() => history.push('student_id_entry?order=yearbook_order'), 75)
    } else {
      const cart = new Cart({ school: chosenSchool, schoolId: chosenSchool.id, email })

      cart.save().then(() => {
        const key = isMakingAppt ? 'apptCart' : 'cart'

        setCustomer({
          ...customer, [key]: cart, email, currentSchool: chosenSchool,
        })

        setTimeout(() => history.push('student_id_entry?order=yearbook_order'), 75)
      })
    }
  }

  const handlePanoramaOrder = () => {
    const chosenSchool = schools.find((item) => item.name.trim() === school.trim())

    if (!chosenSchool) {
      setErrors(['Please select a school from the dropdown'])
      return
    }

    if (!validateEmail(email)) {
      setErrors(['Please enter a valid email'])
      return
    }

    if (errors.length > 0) setErrors([])

    if (existingCart) {
      setCustomer({
        ...customer, cart: existingCart, email, currentSchool: chosenSchool,
      })

      setTimeout(() => history.push('student_id_entry?order=panorama'), 75)
    } else {
      const cart = new Cart({ school: chosenSchool, schoolId: chosenSchool.id, email })

      cart.save().then(() => {
        const key = isMakingAppt ? 'apptCart' : 'cart'

        setCustomer({
          ...customer, [key]: cart, email, currentSchool: chosenSchool,
        })

        setTimeout(() => history.push('student_id_entry?order=panorama'), 75)
      })
    }
  }

  return (
    <form onSubmit={submitHandler}>
      <Grid container maxWidth="sm" margin="auto">
        <Grid item xs={12}>
          <Typography tag="h3" weight="bold" align="center" classes={styles.formHeader}>
            Purchase Portraits & Yearbooks
          </Typography>
        </Grid>
        {errors.length > 0 && (
          <Grid container item xs={12}>
            <Grid item xs={12} md={6}>
              {errors.map((error) => (
                <Typography key={error} weight={600} classes={styles.errorText}>{error}</Typography>
              ))}
            </Grid>
          </Grid>
        )}
        <Grid container item>
          <Grid container item xs={4} alignItems="center">
            <Image src={frontPageFormImg} />
          </Grid>
          <Grid container item alignItems="center" classes={styles.inputsContainer}>
            <Grid container direction="column">
              <Grid item xs classes={styles.inputWrapper}>
                <label htmlFor="school-input" className={styles.label}>
                  School Name
                  <Select
                    id="school-input"
                    defaultValue={customer.currentSchool ? { value: customer.currentSchool.id, label: customer.currentSchool.name } : ''}
                    options={schools.map((selectSchool) => (
                      ({ value: selectSchool.id, label: selectSchool.name })
                    ))}
                    className={styles.schoolInput}
                    styles={customStyles}
                    onChange={schoolHandler}
                  />
                </label>
              </Grid>
              <Grid item xs classes={styles.inputWrapper}>
                <TextInput required val={email} type="email" onChange={emailHandler} label="Email Address" fullWidth classes={styles.input} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container item xs={12} classes={styles.btnContainer} spacing={2}>
          <Grid item xs={6}>
            <Button fullWidth variant="submit" type="submit" onClick={handlePreorder}>Order School Pictures</Button>
          </Grid>
          <Grid item xs={6}>
            <Button fullWidth variant="submit" type="button" onClick={handleYearbookOrder}>Order Yearbooks</Button>
          </Grid>
          <Grid item xs={6}>
            <Button fullWidth variant="submit" onClick={handleOrderStatus}>
              Check Order Status
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button fullWidth variant="submit" type="button" onClick={handlePanoramaOrder}>
              Order Groups and Panorama
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <HomeCTASection />
        </Grid>
      </Grid>
    </form>
  )
}

export default HomePageForm
